/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import classNames from 'classnames';
import {getQueryValue} from '@exc/url/query';
import {injectIntl} from 'react-intl';
import metrics from '@adobe/exc-app/metrics';
import {NOOP} from '@exc/shared';
import PropTypes from 'prop-types';
import React, {Suspense} from 'react';
import {RELEASE_TYPE} from '@exc/core/src/models/Solution';
import ShellWait from './ShellWait';
import {trackOmega} from '@exc/events';
import './Shell.scss';

const ShellNetworkError = React.lazy(() => import('./ShellNetworkError'));
const ShellSidebar = React.lazy(() => import('./ShellSidebar'));
// For local testing of ShellNetworkError, uncomment below
// setTimeout(() => import('./ShellNetworkError'), 5000);

const SEARCH_MAX_WIDTH = 1250;
const MEDIUM_MAX_WIDTH = 950;
const SMALL_MAX_WIDTH = 700;
const X_SMALL_MAX_WIDTH = 575;

const showMoreActionsBreakpoints = {
  feedbackButton: {
    noWorkspaces: 930,
    workspaces: 1090
  },
  noFeedbackButton: {
    noWorkspaces: 800
  }
};

function isSidebarEnabled(featureFlags, sandboxName) {
  if (getQueryValue('aiChatEnabled') === 'true') {
    return true;
  }

  const sandboxFlag = featureFlags?.['shell-ai-chat-enabled-by-sandbox'] || '';
  const enabledSandboxes = sandboxFlag.split(',');
  return enabledSandboxes.indexOf(sandboxName) > -1;
}

function getBusinessApp(parentId, appId) {
  if (appId === 'shell-testapp') {
    return 'AEP';
  }
  return parentId || appId;
}

/**
 * Shell Super component for Experience Cloud UI.
 */

const shellMetrics = metrics.create('exc.shell.container');

class Shell extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasDeferredComponents: false,
      isNotOutlined: true,
      isSidebarOpen: false,
      maxFullSearchSize: false,
      showMoreActionsButton: false,
      size: 'L'
    };
    this.aiToggleRef = React.createRef();
  }

  loadDeferred() {
    this.props.deferredComponentsPromise?.then(deferredComponents => {
      this.deferredComponents = deferredComponents;
      this.setState({hasDeferredComponents: true});
    });
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    trackOmega('shell.omega', window, Boolean(getQueryValue('debugOmega')));

    this.onResize();
    this.loadDeferred();
  }

  componentDidUpdate(prevProps) {
    // Workspaces update late sometimes, so need to run again when updated.
    const {workspaces} = this.props;
    const resize = (
      prevProps.accessOverridesEnabled !== this.props.accessOverridesEnabled ||
      prevProps.sandbox !== this.props.sandbox ||
      prevProps.workspaces && workspaces && prevProps.workspaces.length !== workspaces.length
    );
    if (resize) {
      this.onResize();
    }
    if (!prevProps.deferredComponentsPromise) {
      this.loadDeferred();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  onToggleSidebar = (query, forceOpen = false) => {
    const isSidebarOpen = forceOpen ? true : !this.state.isSidebarOpen;
    this.setState({
      initialAIQuery: query,
      isSidebarOpen
    });
    isSidebarOpen || this.aiToggleRef.current?.focus();
    shellMetrics.event('ai.chat.sidebar.open', {force: forceOpen, isSidebarOpen});
  };

  /**
   * Gets the number of labels.
   * @returns {number} Number of labels.
   */
  getNumLabels() {
    const {accessOverridesEnabled, customEnvLabel, devMode, environment} = this.props;
    let numLabels = customEnvLabel?.length || 0;
    if (devMode) {
      numLabels++;
    }
    if (accessOverridesEnabled) {
      numLabels += 2;
    }
    if (['dev', 'qa', 'stage', 'test'].includes(environment)) {
      numLabels++;
    }
    return numLabels;
  }

  /**
   * Handler for browser resize event.
   * @listens Event window's resize event.
   */
  onResize = () => {
    const width = window.innerWidth;
    const {maxFullSearchSize, showMoreActionsButton, size} = this.state;
    const {feedback, workspaces} = this.props;
    let newSize;
    let maxSearchSize = false;

    if (width > MEDIUM_MAX_WIDTH) {
      newSize = 'L';
    } else if (width <= X_SMALL_MAX_WIDTH) {
      newSize = 'XS';
    } else if (width <= SMALL_MAX_WIDTH) {
      newSize = 'S';
    } else {
      newSize = 'M';
    }

    if (width <= SEARCH_MAX_WIDTH) {
      maxSearchSize = true;
    }

    const newState = {};

    if (newSize !== size) {
      newState.size = newSize;
    }

    if (maxSearchSize !== maxFullSearchSize) {
      newState.maxFullSearchSize = maxSearchSize;
    }

    // Cases where More Actions button should be visible.
    const {feedbackButton, noFeedbackButton} = showMoreActionsBreakpoints;
    const numLabels = this.getNumLabels();
    const hasSandboxSwitcher = !this.props.sandbox?.incognito && this.props.sandbox.enabled && this.props.sandbox.sandboxes?.length;
    const shouldShowMoreActionsButton = (
      (width <= feedbackButton.workspaces && workspaces.length > 0) ||
      (width <= feedbackButton.noWorkspaces && workspaces.length <= 0 && feedback && feedback.enabled) ||
      (width <= noFeedbackButton.noWorkspaces && workspaces.length <= 0 && (!feedback || !feedback.enabled)) ||
      ((numLabels > 2 && width <= 1050) || (numLabels > 1 && width <= X_SMALL_MAX_WIDTH)) ||
      (width <= 1050 && ((feedback && hasSandboxSwitcher) || (numLabels > 1 && (feedback || hasSandboxSwitcher))))
    );

    // Check that current state is not already equal to new value
    if (showMoreActionsButton !== shouldShowMoreActionsButton) {
      newState.showMoreActionsButton = !!shouldShowMoreActionsButton;
    }

    Object.keys(newState) && this.setState(newState);
  };

  render() {
    const {
      accessOverridesEnabled,
      appContainer,
      appId,
      appRoot,
      authId,
      backgroundReady,
      basePath,
      children,
      closeLanguagePicker,
      coachMark,
      currentActiveTenantId,
      customEnvLabel,
      customHeroClick,
      customProfileButtons,
      customSearch,
      devMode,
      disableShellFeedbackButton,
      environment,
      featureFlags,
      feedback,
      getCapabilityWithIconUrl,
      globalSearch,
      hasAccountClusterData,
      helpCenterConfig,
      imsOrgs,
      internal,
      intl,
      isImpersonating,
      landingpage,
      locale,
      localeData,
      modal,
      networkErrorProps,
      noAccess,
      onCustomProfileButtonClick,
      onGlobalDialogOpen,
      onImsOrgChange,
      onLanguageChange,
      onSetUserTheme,
      onSignOutClick,
      onSolutionClick,
      orgToSubOrgsMap,
      parentId,
      profile,
      pulse,
      preferredLanguages,
      recentSandboxes,
      registerKeyHandler,
      releaseType,
      sandbox,
      selectedImsOrg,
      selectedSideNavItemId,
      selectedSubOrg,
      selectedWorkspaceId,
      serviceEnvironment,
      services,
      settingsData,
      settingsService,
      showDebugModal,
      showLanguagePicker,
      showToast,
      sideNavConfig,
      sideNavEnabled,
      sideNavOpen,
      sideNavOpenByHover,
      solutionIcon,
      solutionReleaseType,
      solutions,
      solutionShortTitle,
      solutionTitle,
      subOrgAccessOnlyOnCurrentOrg,
      theme,
      toastPlacement,
      toggleSideNav,
      translationService,
      userAvatar,
      userRecentSandboxesHandler,
      userToken,
      workHubResponse,
      workspaces
    } = this.props;
    const aiSidebarEnabled = isSidebarEnabled(featureFlags, sandbox.selected?.name);
    const {hasDeferredComponents, isSidebarOpen, maxFullSearchSize, showMoreActionsButton, size} = this.state;
    const ShellHeader = hasDeferredComponents ? this.deferredComponents.ShellHeader : 'header';
    const ShellHeaderProps = hasDeferredComponents ? {
      accessOverridesEnabled,
      aiSidebarEnabled,
      aiToggleRef: this.aiToggleRef,
      appContainer,
      appId,
      appRoot,
      backgroundReady,
      basePath,
      coachMark,
      currentActiveTenantId,
      customEnvLabel,
      customHeroClick,
      customProfileButtons,
      customSearch,
      devMode,
      disableShellFeedbackButton,
      environment,
      featureFlags,
      feedback,
      globalSearch: {
        ...globalSearch,
        SearchWidget: this.deferredComponents.SearchWidget
      },
      hasAccountClusterData,
      helpCenterConfig,
      imsOrgs,
      internal,
      isImpersonating,
      landingpage,
      locale,
      localeData,
      maxFullSearchSize,
      modal,
      noAccess,
      onCustomProfileButtonClick,
      onGlobalDialogOpen,
      onImsOrgChange,
      onLanguageChange,
      onSetUserTheme,
      onSignOutClick,
      onSolutionClick,
      onToggleSidebar: this.onToggleSidebar,
      orgToSubOrgsMap,
      parentId,
      preferredLanguages,
      pulse,
      recentSandboxes,
      registerKeyHandler,
      sandbox,
      selectedImsOrg,
      selectedSubOrg,
      selectedWorkspaceId,
      serviceEnvironment,
      services,
      settingsData,
      settingsService,
      showDebugModal,
      showMoreActionsButton,
      sidebarOpen: isSidebarOpen,
      sideNavEnabled,
      sideNavOpen,
      sideNavOpenByHover,
      size,
      solutionIcon,
      solutionReleaseType,
      solutions,
      solutionShortTitle,
      solutionTitle,
      subOrgAccessOnlyOnCurrentOrg,
      theme,
      toastPlacement,
      toggleSideNav,
      translationService,
      userAvatar,
      userEmail: profile.email,
      userFirstName: profile.first_name,
      userLastName: profile.last_name,
      userName: profile.name,
      userRecentSandboxesHandler,
      userTitle: profile.job_function,
      userToken,
      workHubResponse,
      workspaces
    } : {
      className: `spectrum-Shell spectrum--${theme || 'lightest'} spectrum-Shell-headingContainer`
    };

    let AlertsContainer = null;
    if (hasDeferredComponents) {
      AlertsContainer = this.deferredComponents.AlertsContainer;
    }

    const ShellSideNav = hasDeferredComponents ? this.deferredComponents.ShellSideNav : () => null;
    if (!disableShellFeedbackButton) {
      // If the feedback button hasn't been explicitly disabled,
      // check if the pre-release values are Alpha or Beta.
      // The order of the release types is important here.
      ShellHeaderProps.feedback = [releaseType, solutionReleaseType].reduce((button, release) => {
        if (button) {
          // If we already have a button, then we're done
          return button;
        }

        if ([RELEASE_TYPE.ALPHA, RELEASE_TYPE.BETA].includes(release)) {
          // If the pre-release value is Alpha or Beta, then we need to show the button
          return release === RELEASE_TYPE.ALPHA ? {
            buttonLabel: intl.formatMessage({defaultMessage: 'Alpha Feedback', description: 'Alpha feedback button label', id: 'alphaFeedback'}),
            enabled: true,
            type: 'openFeedback'
          } : {
            buttonLabel: intl.formatMessage({defaultMessage: 'Beta Feedback', description: 'Beta feedback button label', id: 'betaFeedback'}),
            enabled: true,
            type: 'openFeedback'
          };
        }
      }, undefined);
    }

    // Add magenta border
    isImpersonating ? document.body.classList.add('impersonation') : document.body.classList.remove('impersonation');

    const businessApp = getBusinessApp(parentId, appId);
    return (
      <div
        className="spectrum-ShellContainer"
        data-omega-feature="shell header"
        data-omega-widget="shell header buttons"
        data-omega-widget-type="button">
        {networkErrorProps?.showNetworkDisconnected &&
          <Suspense fallback={null}>
            <ShellNetworkError {...networkErrorProps} />
          </Suspense>
        }
        <ShellHeader {...ShellHeaderProps} />
        <div className={classNames('spectrum-ShellContainer-content', {
          'can-takeover': appContainer,
          'has-appContainer': appContainer,
          'modal-open': modal
        })}>
          {hasDeferredComponents && showLanguagePicker &&
            <Suspense fallback={<ShellWait />}>
              <this.deferredComponents.ShellLanguagePicker
                authId={authId}
                closeLanguagePicker={closeLanguagePicker}
                locale={locale}
                onLanguageChange={onLanguageChange}
                serviceEnvironment={serviceEnvironment}
                showLanguagePicker={showLanguagePicker}
                userToken={userToken}
              />
            </Suspense>
          }
          {sideNavConfig &&
            <Suspense fallback={null}>
              <ShellSideNav
                appContainer={appContainer}
                appId={appId}
                env={environment}
                featureFlags={featureFlags}
                getCapabilityWithIconUrl={getCapabilityWithIconUrl}
                isOpen={sideNavEnabled && sideNavOpen}
                locale={locale}
                modal={modal}
                orgId={selectedImsOrg}
                selectedSideNavItemId={selectedSideNavItemId}
                settingsService={settingsService}
                sideNavConfig={sideNavConfig}
                sideNavOpenByHover={sideNavOpenByHover}
                theme={theme}
                toggleSideNav={toggleSideNav}
                userId={profile.authId}
                workHubResponse={workHubResponse}
              />
            </Suspense>
          }
          {children}
          {AlertsContainer &&
            <Suspense fallback={null}>
              <AlertsContainer theme={theme} />
            </Suspense>}
          {aiSidebarEnabled && (
            <Suspense fallback={null}>
              <ShellSidebar
                appContainer={appContainer}
                authId={profile.authId}
                avatar={profile.avatar}
                businessApp={businessApp}
                email={profile.email || ''}
                env={environment}
                initialQuery={this.state.initialAIQuery}
                onClose={this.onToggleSidebar}
                open={this.state.isSidebarOpen}
                orgId={selectedImsOrg}
                reviewToolEnabled={featureFlags?.['co-pilot-review-enabled'] === 'true'}
                sandboxName={sandbox.selected?.name}
                settingsService={settingsService}
                showToast={showToast}
              />
            </Suspense>
          )}
        </div>
      </div>
    );
  }
}

Shell.propTypes = {
  appContainer: PropTypes.string,
  backgroundReady: PropTypes.bool,
  basePath: PropTypes.string,
  currentActiveTenantId: PropTypes.string,
  customButtons: PropTypes.object,
  customHeroClick: PropTypes.func,
  customSearch: PropTypes.shape({
    enabled: PropTypes.bool,
    onClick: PropTypes.func,
    open: PropTypes.bool
  }),
  devMode: PropTypes.bool,
  disableShellFeedbackButton: PropTypes.bool,
  featureFlags: PropTypes.object,
  getCapabilityWithIconUrl: PropTypes.func,
  hasAccountClusterData: PropTypes.bool,
  helpCenterConfig: PropTypes.shape({
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        href: PropTypes.string,
        icon: PropTypes.string,
        label: PropTypes.string
      })
    )
  }),
  imsOrgs: PropTypes.arrayOf(
    PropTypes.shape({
      imsOrgId: PropTypes.string,
      imsOrgName: PropTypes.string,
      tenantId: PropTypes.string
    })
  ).isRequired,
  landingpage: PropTypes.shape({
    description: PropTypes.string,
    href: PropTypes.string,
    longname: PropTypes.string,
    name: PropTypes.string,
    visible: PropTypes.bool
  }),
  locale: PropTypes.string,
  networkErrorProps: PropTypes.object,
  noAccess: PropTypes.bool,
  onCustomButtonClick: PropTypes.func,
  onImsOrgChange: PropTypes.func,
  onSignOutClick: PropTypes.func,
  onSolutionClick: PropTypes.func,
  recentSandboxes: PropTypes.array,
  releaseType: PropTypes.oneOf(Object.values(RELEASE_TYPE)),
  sandbox: PropTypes.shape({
    enabled: PropTypes.bool,
    onChange: PropTypes.func,
    sandboxes: PropTypes.array,
    selected: PropTypes.object
  }),
  selectedImsOrg: PropTypes.string,
  selectedSubOrg: PropTypes.object,
  selectedWorkspaceId: PropTypes.string,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      enabled: PropTypes.bool,
      href: PropTypes.string,
      learnMoreLink: PropTypes.string,
      longname: PropTypes.string,
      name: PropTypes.string,
      visible: PropTypes.bool
    })
  ),
  settingsData: PropTypes.object,
  settingsService: PropTypes.object,
  showDebugModal: PropTypes.func,
  solutionIcon: PropTypes.string,
  solutionReleaseType: PropTypes.oneOf(Object.values(RELEASE_TYPE)),
  solutions: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      enabled: PropTypes.bool,
      href: PropTypes.string,
      learnMoreLink: PropTypes.string,
      longname: PropTypes.string,
      name: PropTypes.string,
      visible: PropTypes.bool
    })
  ),
  solutionShortTitle: PropTypes.string,
  solutionTitle: PropTypes.string,
  userAvatar: PropTypes.string,
  userFirstName: PropTypes.string,
  userLastName: PropTypes.string,
  userName: PropTypes.string,
  userRecentSandboxesHandler: PropTypes.func,
  userTitle: PropTypes.string,
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string
    })
  )
};

Shell.defaultProps = {
  appContainer: null,
  backgroundReady: false,
  currentActiveTenantId: '',
  customButtons: [],
  customSearch: {
    enabled: false,
    onClick: () => {},
    open: false
  },
  hasAccountClusterData: false,
  helpCenterConfig: {},
  landingpage: {},
  locale: 'en-US',
  noAccess: false,
  onCustomButtonClick: NOOP,
  onImsOrgChange: NOOP,
  onSignOutClick: NOOP,
  onSolutionClick: NOOP,
  releaseType: RELEASE_TYPE.GA,
  sandbox: {
    enabled: false,
    onChange: NOOP,
    sandboxes: [],
    selected: null
  },
  selectedWorkspaceId: '',
  services: [],
  showDebugModal: null,
  solutionReleaseType: RELEASE_TYPE.GA,
  solutions: [],
  solutionShortTitle: 'AEC',
  solutionTitle: 'Adobe Experience Cloud',
  userAvatar: undefined,
  userName: 'Adobe User',
  userTitle: undefined,
  workspaces: []
};

export default injectIntl(Shell);
